import React from 'react';
import PropTypes from 'prop-types';
import Select from 'react-select';

// Forecast release status filter — colored dot per state for quick scanning.
//   All status → neutral gray   Draft → amber (not released)   Released → green
export const STATUS_OPTIONS = [
  { value: '',         label: 'All status', color: '#9ca3af' },
  { value: 'draft',    label: 'Draft',      color: '#f59e0b' },
  { value: 'released', label: 'Released',   color: '#10b981' },
];

// Tinted pill + dot for the grid Status cell — same color language as the
// filter dots above, so the column and the filter read as one system.
const BADGE_META = {
  released: { label: 'Released', dot: '#10b981', bg: '#ecfdf5', fg: '#047857' },
  draft:    { label: 'Draft',    dot: '#f59e0b', bg: '#fffbeb', fg: '#b45309' },
};

export const StatusBadge = ({ status }) => {
  const meta = BADGE_META[status] || BADGE_META.draft;
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '2px 10px',
        borderRadius: 9999,
        backgroundColor: meta.bg,
        color: meta.fg,
        fontSize: 11,
        fontWeight: 600,
        lineHeight: 1.7,
      }}
    >
      <span
        aria-hidden
        style={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: meta.dot, flexShrink: 0 }}
      />
      {meta.label}
    </span>
  );
};
StatusBadge.propTypes = { status: PropTypes.oneOf(['released', 'draft']).isRequired };

const Dot = ({ color }) => (
  <span
    aria-hidden
    style={{
      width: 8,
      height: 8,
      borderRadius: '50%',
      backgroundColor: color,
      flexShrink: 0,
      boxShadow: '0 0 0 2px rgba(0,0,0,0.04)',
    }}
  />
);
Dot.propTypes = { color: PropTypes.string.isRequired };

const formatOptionLabel = (opt) => (
  <div className="tw-flex tw-items-center tw-gap-2">
    <Dot color={opt.color} />
    <span>{opt.label}</span>
  </div>
);

const styles = {
  control: (b, s) => ({
    ...b,
    minHeight: 30,
    fontSize: 12,
    borderColor: s.isFocused ? '#6366f1' : '#d1d5db',
    boxShadow: s.isFocused ? '0 0 0 2px #ede9fe' : 'none',
    '&:hover': { borderColor: '#6366f1' },
  }),
  valueContainer:   (b) => ({ ...b, padding: '0 8px' }),
  dropdownIndicator:(b) => ({ ...b, padding: 4, color: '#9ca3af' }),
  indicatorSeparator: () => ({ display: 'none' }),
  singleValue:      (b) => ({ ...b, fontWeight: 500, color: '#374151' }),
  menu:             (b) => ({ ...b, zIndex: 1050, fontSize: 12, overflow: 'hidden' }),
  menuPortal:       (b) => ({ ...b, zIndex: 1050 }),
  option: (b, s) => ({
    ...b,
    padding: '7px 10px',
    cursor: 'pointer',
    backgroundColor: s.isSelected ? '#eef2ff' : s.isFocused ? '#f3f4f6' : '#fff',
    color: '#111827',
    fontWeight: s.isSelected ? 600 : 400,
  }),
};

const StatusSelect = ({ value, onChange, width, menuPortalTarget }) => {
  const selected = STATUS_OPTIONS.find((o) => o.value === (value || '')) || STATUS_OPTIONS[0];

  return (
    <Select
      options={STATUS_OPTIONS}
      value={selected}
      onChange={(opt) => onChange(opt ? opt.value : '')}
      formatOptionLabel={formatOptionLabel}
      classNamePrefix="status"
      isSearchable={false}
      menuPortalTarget={menuPortalTarget ?? document.body}
      styles={{ ...styles, control: (b, s) => ({ ...styles.control(b, s), width: width || 130 }) }}
    />
  );
};

StatusSelect.propTypes = {
  value:            PropTypes.string,
  onChange:         PropTypes.func.isRequired,
  width:            PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  menuPortalTarget: PropTypes.object,
};

export default StatusSelect;
